import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { motion } from "framer-motion";

const faqs = [
  {
    question: "What is the best marketing for tradies in Australia?",
    answer:
      "The best marketing for trade businesses combines local SEO, Google Business Profile optimisation, targeted Google Ads and Facebook Ads, a professional mobile-first website, and an automated 5-star review collection system. Our digital growth system integrates all of these into one seamless strategy, designed specifically for Australian trade and construction businesses.",
  },
  {
    question: "How much does digital marketing cost for trade businesses?",
    answer:
      "Pricing depends on what your business needs right now. After a quick audit, we give a clear proposal with exact costs, timelines and outcomes — no hidden fees, no surprises. Most clients see measurable results within 2-4 weeks, with ROI from increased enquiries and booked jobs.",
  },
  {
    question: "How fast will I see results from trade marketing?",
    answer:
      "Most clients see improvement within 2–4 weeks. Foundations (Google Business Profile, reviews, website) typically lift performance immediately, while Google Ads, Facebook Ads and automation compound over time to deliver consistent, predictable growth.",
  },
  {
    question: "How can trade businesses get more Google reviews?",
    answer:
      "We implement an automated review collection system that sends review requests at the perfect time, making it easy for happy customers to leave 5-star reviews. Most Australian trade businesses see their Google reviews triple within 2 months, building trust and improving local search rankings.",
  },
  {
    question: "Do you work with tradies and construction businesses Australia-wide?",
    answer:
      "Yes! We work with trade, construction and home service businesses across all of Australia — including Sydney, Melbourne, Brisbane, Perth, Adelaide, Gold Coast, Newcastle, Canberra and regional areas. Our digital systems are designed to dominate your local suburbs.",
  },
  {
    question: "How do I get more leads as a tradie?",
    answer:
      "To get more quality leads, you need three things: visibility (be found first in your target suburbs), trust (professional website, strong Google reviews), and speed (respond to enquiries fast). Our growth system addresses all three to deliver consistent, qualified leads every week.",
  },
  {
    question: "Do I need everything, or can I start small?",
    answer:
      "You can start with whichever pillar matters most — Foundation, Growth, Automation or Scale. The system is modular and built to expand as your business grows. We'll recommend where to start based on your current digital setup.",
  },
  {
    question: "How are you different from a normal marketing agency?",
    answer:
      "We work exclusively with Trades, Construction & Home Services businesses. No generic campaigns, no shared leads, and no jargon — just a proven digital growth system that fits how quoting-based businesses actually operate in Australia.",
  },
  {
    question: "Will this attract tyre-kickers or low-quality enquiries?",
    answer:
      "No. We qualify enquiries by suburb, job type and intent before they reach you, giving you more of the high-value work you want — and less of the price shoppers you don't. Our targeting is built specifically for Australian trade businesses.",
  },
  {
    question: "What trades and construction businesses do you work with?",
    answer:
      "We specialise in: electricians, plumbers, builders, roofers, kitchen & bathroom renovators, landscapers, painters, HVAC technicians, fencing contractors, pool builders, remedial builders, and all home service businesses across Australia.",
  },
  {
    question: "How involved do I need to be?",
    answer:
      "Minimal. We handle the build, setup and optimisation. You focus on quoting and delivering quality work while your digital engine runs consistently in the background, generating leads and booking jobs.",
  },
  {
    question: "Do you offer Google Ads and Facebook Ads for trade businesses?",
    answer:
      "Yes, we provide targeted Google Ads and Facebook Ads campaigns specifically designed for trade and construction businesses. Our ads target customers actively searching for services in your suburbs, with proper lead qualification to filter out time-wasters.",
  },
];

export function FAQSection() {
  return (
    <section id="faq-section" className="section-padding bg-muted relative overflow-hidden" aria-labelledby="faq-heading">
      {/* Background accent */}
      <div className="absolute inset-0 grid-pattern opacity-40" />

      <div className="container-wide relative">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-3xl mx-auto text-center mb-6 sm:mb-8"
        >
          <div className="section-badge mb-3 sm:mb-4 text-xs">
            <span>Frequently Asked Questions</span>
          </div>
          <h2 id="faq-heading" className="heading-section text-foreground">
            Common Questions About Marketing for Trade Businesses
          </h2>
          <p className="text-muted-foreground text-base mt-3 max-w-xl mx-auto">
            Got questions? Here's what Australian trade, construction and home service businesses commonly ask about digital marketing.
          </p>
        </motion.div>

        {/* FAQ Accordion */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-2xl mx-auto"
        >
          <Accordion type="single" collapsible className="space-y-2.5">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="premium-card px-4 sm:px-5 data-[state=open]:shadow-card-hover data-[state=open]:border-primary/20"
              >
                <AccordionTrigger className="text-left text-[0.9375rem] sm:text-base font-semibold text-foreground hover:no-underline py-4 [&[data-state=open]>svg]:rotate-180">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground pb-4 leading-[1.7] text-[0.9375rem] sm:text-base">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>

        {/* AEO Summary */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8 text-center"
        >
          <p className="text-base text-muted-foreground">
            Still have questions? <a href="#contact-section" className="text-primary font-semibold hover:underline">Book a free strategy call</a> and we'll answer everything.
          </p>
        </motion.div>
      </div>
    </section>
  );
}
